import React, { useState, useMemo } from 'react';
import { css } from '@emotion/react';
import { SecondaryButton } from '@/styles/blocks';
import { colors } from '@/styles';
import { OnwardCheckbox } from '@/components/ShipmentForm/blocks';
import { FormControlLabel, Grid, Switch } from '@material-ui/core';
import { ResponsiveSidebarDialog, StickyModalActions, ModalContent, PrimaryButton } from '@/styles/blocks';
import { ItemCheckbox, Body1, Header1 } from '../Crossdocking/blocks';

const CreateReturnModal = ({ open, items = [], callbacks }) => {
    const [selectedItems, setSelectedItems] = useState(Object.fromEntries(items.map((item) => [item.item_id, true])));
    // When on, the return is created with no PO so it can't collide on the (shipper_id, po_number) unique index.
    const [clearPoNumber, setClearPoNumber] = useState(false);

    const resetForm = () => {
        setSelectedItems(Object.fromEntries(items.map((item) => [item.item_id, true])));
        setClearPoNumber(false);
    };

    const isValid = useMemo(() => {
        return Object.values(selectedItems).some((selected) => selected);
    }, [selectedItems]);

    const getSelectedItems = () => {
        return items.filter((item) => selectedItems[item.item_id]);
    };

    const areAllSelected = useMemo(() => {
        return items.every((item) => selectedItems[item.item_id]);
    }, [items, selectedItems]);

    return (
        <ResponsiveSidebarDialog
            open={open}
            onClose={() => {
                resetForm();
                callbacks.onClose();
            }}
        >
            <ModalContent
                css={css`
                    width: 100%;
                `}
            >
                <Grid
                    direction="row"
                    container
                    css={css`
                        margin-bottom: 20px;
                    `}
                >
                    <Header1>Create Return Order</Header1>
                </Grid>

                <Grid
                    direction="row"
                    container
                    css={css`
                        margin-bottom: 8px;
                        color: ${colors.greys[0]};
                    `}
                >
                    <Body1>Select which items to include in the return order:</Body1>
                </Grid>

                <Grid
                    direction="row"
                    container
                    css={css`
                        margin-bottom: 30px;
                    `}
                >
                    <Grid direction="column" container>
                        <Grid
                            direction="row"
                            container
                            item
                            css={css`
                                margin-bottom: 8px;
                            `}
                        >
                            <Grid
                                direction="column"
                                container
                                css={css`
                                    flex: 0;
                                    flex-basis: 0;
                                    justify-content: center;
                                `}
                            >
                                <OnwardCheckbox
                                    checked={areAllSelected}
                                    onChange={() => {
                                        const newState = !areAllSelected;
                                        setSelectedItems(
                                            Object.fromEntries(items.map((item) => [item.item_id, newState]))
                                        );
                                    }}
                                />
                            </Grid>
                            <Grid
                                direction="column"
                                container
                                css={css`
                                    flex: 1;
                                    flex-basis: 0;
                                    justify-content: center;
                                `}
                            >
                                <Header1>Select All</Header1>
                            </Grid>
                        </Grid>

                        {items.map((item) => (
                            <ItemCheckbox
                                key={item.item_id}
                                item={item}
                                checked={selectedItems[item.item_id]}
                                callbacks={{
                                    onCheck: (id) =>
                                        setSelectedItems((prev) => ({
                                            ...prev,
                                            [id]: !prev[id],
                                        })),
                                }}
                            />
                        ))}
                    </Grid>
                </Grid>

                <Grid
                    direction="row"
                    container
                    alignItems="center"
                    css={css`
                        margin-bottom: 8px;
                    `}
                >
                    <FormControlLabel
                        control={
                            <Switch
                                checked={clearPoNumber}
                                onChange={(e) => setClearPoNumber(e.target.checked)}
                                color="primary"
                            />
                        }
                        label={<Body1>Leave PO# blank for RMA entry</Body1>}
                    />
                </Grid>
            </ModalContent>
            <StickyModalActions>
                <Grid
                    container
                    direction="column"
                    css={css`
                        flex: 0;
                    `}
                >
                    <SecondaryButton
                        onClick={() => {
                            resetForm();
                            callbacks.onClose();
                        }}
                    >
                        Cancel
                    </SecondaryButton>
                </Grid>
                <Grid
                    container
                    direction="column"
                    css={css`
                        flex: 0;
                    `}
                >
                    <PrimaryButton
                        disabled={!isValid}
                        onClick={() => {
                            callbacks.onSubmit(getSelectedItems(), clearPoNumber);
                            resetForm();
                        }}
                    >
                        Create Return Order
                    </PrimaryButton>
                </Grid>
            </StickyModalActions>
        </ResponsiveSidebarDialog>
    );
};

export default CreateReturnModal;
